import {
    AbstractPipeHost,
    EnrichmentPipeGroup,
    EnrichmentPipeSet,
    IPulsePluginContext,
    PipeGroup,
    PipeSet,
    PulseMessage,
    TransformPipeGroup,
    TransformPipeSet,
} from '@pulse/common';

export class PipeHost extends AbstractPipeHost<PulseMessage, IPulsePluginContext> {
    protected transformPipes: Array<TransformPipeSet<any>> = [];
    protected enrichmentPipes: Array<EnrichmentPipeSet<any>> = [];

    process<T extends PulseMessage>(message: T, context: IPulsePluginContext): any[] {
        return this.transform(message, context).map((msg) => this.enrich(msg, context));
    }

    transform<T extends PulseMessage>(message: T, context: IPulsePluginContext): any[] {
        const pipes = this.selectPipeSet<TransformPipeGroup>(message, this.transformPipes);
        let messages: PulseMessage[] = [message];

        for (const pipe of pipes) {
            if (!pipe.transform) {
                continue;
            }
            const transformedMessages: PulseMessage[] = [];

            for (const msg of messages) {
                const pipeTransformedMessages = pipe.transform(msg, context);
                transformedMessages.push(...pipeTransformedMessages);
            }

            messages = transformedMessages;
        }

        return messages;
    }

    enrich<T extends PulseMessage>(message: T, context: IPulsePluginContext) {
        return this.selectPipeSet<EnrichmentPipeGroup>(message, this.enrichmentPipes)
            .reverse()
            .reduce((msg, pipe): T => pipe.enrich(msg, context) as T, message);
    }

    registerTransformSet(set: TransformPipeSet<any>) {
        this.transformPipes.push(set);
    }

    registerEnrichmentSet(set: EnrichmentPipeSet<any>) {
        this.enrichmentPipes.push(set);
    }

    protected selectPipeSet<T extends PipeGroup>(message: PulseMessage, sets: PipeSet<any>[]): T {
        for (const set of sets) {
            const selectorMatched =
                set.selector === '*' ||
                set.selector === message.type ||
                (typeof set.selector === 'function' ? set.selector(message) : false) ||
                false;

            if (!selectorMatched) {
                continue;
            }

            const blacklistMatched =
                typeof set.exclude === 'function' ? set.exclude(message) : false;

            if (!blacklistMatched) {
                return [...set.use] as T;
            }
        }

        return [] as any;
    }
}
